import React from 'react';

import Wahanda from 'common/wahanda';
import { DialogFooter, DialogFooterButton } from 'components/common/DialogFooter';
import { Expandable } from 'components/common/Expandable';
import Dialog from 'components/common/react-dialog';
import { track } from 'src/tracking/trackingService';

import type { ImportStatus } from '../reducer';
import { ClientImportAnalytics } from '../tracking';
import { FileImportStatus } from '../types';
import type { ClientImportEntryContext } from '../types/entryContext';
import style from './ClientsImportFinish.scss';
import { StatusMessage } from './StatusMessage';

const lang = Wahanda.lang.clientsImport.addMultipleClients.finish;

export interface Props {
  importStatus: ImportStatus;
  canAccessAppCampaign: boolean;
  onClose: () => void;
  refreshClientsList: () => void;
  entryContext?: ClientImportEntryContext | null;
  actions: {
    clientsImportReset: () => void;
    clientsImportToCampaignPrompt: () => void;
  };
}

export class ClientsImportFinish extends React.PureComponent<Props> {
  public render() {
    return (
      <Dialog
        dataTest="clients-import-finish-modal"
        noContentPadding
        onClose={this.onClose}
        restrictHeight
        title={this.getDialogTitle()}
        width={500}
      >
        <div className={style.container}>
          <StatusMessage />
          {this.renderMessages()}
        </div>
        {this.renderFooter()}
      </Dialog>
    );
  }

  private renderFooter = () => {
    const { canAccessAppCampaign } = this.props;
    return (
      <DialogFooter>
        <DialogFooterButton
          onClick={this.onClose}
          title={Wahanda.lang.shared.close}
          type="secondary"
        />
        {canAccessAppCampaign && (
          <DialogFooterButton
            dataTest="continue-to-campaign-prompt"
            onClick={this.onContinue}
            title={lang.success.continue}
          />
        )}
      </DialogFooter>
    );
  };

  private renderMessage = ({ message }) => (
    <div className={style.warningMessage} key={message}>
      {message}
    </div>
  );

  private renderMessages = () => {
    const { messages } = this.props.importStatus;

    if (!messages || !messages.length) {
      return null;
    }

    return (
      <div className={style.expandable}>
        <Expandable
          maxHeight={200}
          title={
            <div data-test="warnings-title">
              {Wahanda.Template.render(lang.warnings, {
                count: messages.length,
              })}
            </div>
          }
        >
          <div data-test="warnings-children">{messages.map(this.renderMessage)}</div>
        </Expandable>
      </div>
    );
  };

  private getDialogTitle = (): string | undefined => {
    const { status, importCount } = this.props.importStatus;

    if (status) {
      track('clients.management.client_list_upload.result', {
        import_list_count: importCount ?? 0,
        import_status: status,
        source: this.props.entryContext!.source,
      });
    }

    switch (status) {
      case FileImportStatus.FINISHED_SUCCESS:
      case FileImportStatus.FINISHED_WITH_WARNINGS:
        return lang.clientsAdded;
      default:
    }
  };

  private onClose = () => {
    this.props.actions.clientsImportReset();
    this.props.refreshClientsList();
    this.props.onClose();
  };

  private onContinue = () => {
    ClientImportAnalytics.trackContinueToCampaignPromptClicked();
    this.props.actions.clientsImportToCampaignPrompt();
  };
}
